import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { PageHero, Reveal, PhotoFrame, EmptyState, Tag, EASE } from "@/components/Shared";
import { useCollection, fmtDateShort, IMG } from "@/lib/api";

const ALL_CATS = ["Installation", "Projects", "Fellowships", "GBMs", "District Events", "Team", "Behind the Scenes"];

export default function Gallery() {
  const { items, loading } = useCollection("gallery");
  const [cat, setCat] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  const cats = useMemo(() => {
    const present = [...new Set((items || []).map((g) => g.category).filter(Boolean))];
    return ["All", ...new Set([...ALL_CATS.filter((c) => present.includes(c) || cat === c), ...present])];
  }, [items, cat]);

  const filtered = (items || []).filter((g) => cat === "All" || g.category === cat);

  return (
    <main>
      <PageHero eyebrow="Gallery · 2026–27" title="A visual" italic="archive" image={IMG.forestMist}>
        <p className="text-sm leading-relaxed text-silver-dim sm:text-base">
          Instead of hundreds of photos scattered across pages, this is the club's photographic record — organised by
          category, growing all year.
        </p>
      </PageHero>

      <section className="mx-auto max-w-7xl px-6 py-20" data-testid="gallery-section">
        <Reveal>
          <div className="flex flex-wrap gap-3" data-testid="gallery-filters">
            {cats.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                className={`border px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-all duration-300 ${
                  cat === c ? "border-ember bg-ember/10 text-ember" : "border-silver/20 text-silver-muted hover:border-silver/50 hover:text-silver"
                }`}
                data-testid={`gallery-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {c}
              </button>
            ))}
          </div>
        </Reveal>

        {loading && <p className="mt-12 text-sm text-silver-muted">Loading gallery…</p>}

        <div className="mt-12 columns-1 gap-5 sm:columns-2 lg:columns-3">
          {filtered.map((g, i) => (
            <Reveal key={g.id} delay={(i % 3) * 0.06} className="mb-5 break-inside-avoid">
              <button
                onClick={() => g.image_url && setLightbox(g)}
                className="card-line group block w-full p-3 text-left"
                data-testid={`gallery-item-${g.id}`}
              >
                <PhotoFrame src={g.image_url} alt={g.title} label={`${g.title} — photo coming via Admin`} className="aspect-[4/3] w-full" imgClassName="transition-transform duration-700 group-hover:scale-105" />
                <div className="flex items-center justify-between px-2 pb-1 pt-4">
                  <div>
                    <p className="font-display text-lg text-silver">{g.title}</p>
                    <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-silver-muted">{fmtDateShort(g.date)}</p>
                  </div>
                  <Tag>{g.category}</Tag>
                </div>
              </button>
            </Reveal>
          ))}
        </div>

        {!loading && filtered.length === 0 && (
          <div className="mt-12">
            <EmptyState title="No photographs here yet" note="The team will upload photos via the Admin panel." />
          </div>
        )}
      </section>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            className="fixed inset-0 z-[95] flex flex-col items-center justify-center bg-ink/92 p-4 backdrop-blur-xl"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)}
            data-testid="gallery-lightbox"
          >
            <motion.img
              src={lightbox.image_url}
              alt={lightbox.title}
              className="max-h-[78vh] max-w-full object-contain"
              initial={{ scale: 0.94, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.45, ease: EASE }}
            />
            <p className="mt-6 font-display text-2xl text-silver">{lightbox.title}</p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-silver-muted">
              {lightbox.category} · {fmtDateShort(lightbox.date)}
            </p>
            <button onClick={() => setLightbox(null)} className="absolute right-6 top-6 text-silver-muted hover:text-white" aria-label="Close lightbox" data-testid="gallery-lightbox-close-btn">
              <X size={26} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
